<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSourceCardVisibility } from './useSourceCardVisibility';
import Button from 'dashboard/components-next/button/Button.vue';
import { PLATFORMS } from 'dashboard/helper/sourcePlatforms';
import PublishedReply from './parts/PublishedReply.vue';
import ComplaintDialog from './parts/ComplaintDialog.vue';
import FeedbackView from './views/FeedbackView.vue';
import QuestionView from './views/QuestionView.vue';
import ReturnView from './views/ReturnView.vue';
import OrderDialogView from './views/OrderDialogView.vue';
import ReviewView from './views/ReviewView.vue';
import PostCommentView from './views/PostCommentView.vue';
import MentionView from './views/MentionView.vue';
import DirectView from './views/DirectView.vue';
import TelegramGroupView from './views/TelegramGroupView.vue';
import BotView from './views/BotView.vue';
import WebsiteView from './views/WebsiteView.vue';
import EmailView from './views/EmailView.vue';
import {
  getConversationSource,
  findPublishedReply,
  findLastIncomingAt,
} from './helpers';

const props = defineProps({
  conversation: { type: Object, required: true },
  inbox: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const VIEWS = {
  feedback: FeedbackView,
  question: QuestionView,
  return: ReturnView,
  dialog: OrderDialogView,
  review: ReviewView,
  comment: PostCommentView,
  reel_comment: PostCommentView,
  video_comment: PostCommentView,
  channel_comment: PostCommentView,
  mention: MentionView,
  direct: DirectView,
  group_message: TelegramGroupView,
  bot: BotView,
  seller_chat: WebsiteView,
  support: WebsiteView,
  email: EmailView,
};

const KIND_ICONS = {
  feedback: 'i-lucide-star',
  question: 'i-lucide-circle-help',
  return: 'i-lucide-undo-2',
  dialog: 'i-lucide-messages-square',
  review: 'i-lucide-map-pin',
  comment: 'i-lucide-message-circle',
  reel_comment: 'i-lucide-clapperboard',
  video_comment: 'i-lucide-play',
  channel_comment: 'i-lucide-megaphone',
  mention: 'i-lucide-at-sign',
  direct: 'i-lucide-mail',
  group_message: 'i-lucide-users',
  bot: 'i-lucide-bot',
  seller_chat: 'i-lucide-store',
  support: 'i-lucide-life-buoy',
  email: 'i-lucide-mail',
};

const REPLY_MODE_ICONS = {
  thread: 'i-lucide-messages-square',
  single: 'i-lucide-reply',
  window_24h: 'i-lucide-hourglass',
  readonly: 'i-lucide-eye',
};

const collapsed = ref(false);
const { isOpen } = useSourceCardVisibility();
const complaintDialogRef = ref(null);

const source = computed(() => getConversationSource(props.conversation));
const kind = computed(() => source.value?.kind);
const platformKey = computed(
  () => source.value?.platform || props.inbox?.platform || props.inbox?.medium
);
const platform = computed(
  () => PLATFORMS[platformKey.value] || PLATFORMS.website
);
const view = computed(() => VIEWS[kind.value]);

const kindLabel = computed(() => {
  if (!kind.value) return '';
  const key = `MULTICHAT.SOURCE.KINDS.${kind.value.toUpperCase()}`;
  return t(key) === key ? kind.value : t(key);
});

const replyModeLabel = computed(() => {
  const mode = source.value?.reply_mode;
  if (!mode || mode === 'thread') return '';
  return t(`MULTICHAT.SOURCE.REPLY_MODES.${mode.toUpperCase()}`);
});

const messages = computed(() => props.conversation.messages || []);
const publishedReply = computed(() => findPublishedReply(messages.value));
const lastIncomingAt = computed(() => findLastIncomingAt(messages.value));

const supportsReplyEditing = computed(
  () =>
    source.value?.reply_mode === 'single' ||
    ['feedback', 'question', 'review'].includes(kind.value)
);

const isReview = computed(() => kind.value === 'review');
const complaintStatus = computed(
  () => source.value?.review?.complaint?.status || 'none'
);
const canComplain = computed(
  () => isReview.value && ['none', 'rejected'].includes(complaintStatus.value)
);

const summary = computed(() => {
  const s = source.value || {};
  return (
    s.product?.name ||
    s.post?.title ||
    s.post?.text ||
    s.mention?.author ||
    s.telegram?.chat_title ||
    s.website?.seller?.name ||
    s.website?.topic ||
    [s.review?.catalog, s.review?.store?.city, s.review?.store?.address]
      .filter(Boolean)
      .join(' · ') ||
    s.email?.category ||
    s.dialog?.subject ||
    ''
  );
});

const viewProps = computed(() => {
  const base = { source: source.value };
  if (['feedback', 'question', 'return', 'dialog'].includes(kind.value)) {
    base.platform = platformKey.value;
  }
  if (kind.value === 'direct') base.lastIncomingAt = lastIncomingAt.value;
  return base;
});

const openComplaint = () => complaintDialogRef.value?.open();
</script>

<template>
  <section
    v-if="source && view && isOpen"
    class="ltr:ml-3 ltr:mr-14 rtl:mr-3 rtl:ml-14 mt-2 overflow-hidden border rounded-xl border-n-weak bg-n-solid-1 shadow-sm"
  >
    <header
      class="flex items-center gap-2.5 px-3 py-2 cursor-pointer select-none hover:bg-n-alpha-1"
      @click="isOpen = false"
    >
      <span
        class="grid flex-shrink-0 rounded-lg size-7 place-items-center shadow-sm"
        :class="platform.badgeClass"
      >
        <span class="size-4" :class="platform.icon" />
      </span>
      <div class="flex flex-col flex-1 min-w-0">
        <div class="flex items-center gap-1.5 min-w-0">
          <span
            class="text-[13px] font-semibold text-n-slate-12 whitespace-nowrap flex-shrink-0"
          >
            {{ t(platform.name) }}
          </span>
          <span class="text-n-slate-9">·</span>
          <span
            class="inline-flex items-center min-w-0 gap-1 text-[13px] text-n-slate-11"
          >
            <span class="size-3.5 flex-shrink-0" :class="KIND_ICONS[kind]" />
            <span class="truncate">{{ kindLabel }}</span>
          </span>
        </div>
        <span
          v-if="collapsed && summary"
          class="text-[11px] text-n-slate-10 truncate"
        >
          {{ summary }}
        </span>
      </div>
      <span
        v-if="replyModeLabel"
        class="hidden sm:inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-n-alpha-2 text-[11px] font-medium text-n-slate-11 flex-shrink-0"
      >
        <span class="size-3" :class="REPLY_MODE_ICONS[source.reply_mode]" />
        {{ replyModeLabel }}
      </span>
      <a
        v-if="source.url && !isReview"
        v-tooltip.top="t('MULTICHAT.SOURCE.OPEN_ON_PLATFORM')"
        :href="source.url"
        target="_blank"
        rel="noopener noreferrer"
        class="grid flex-shrink-0 rounded-md size-6 place-items-center text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        @click.stop
      >
        <span class="i-lucide-external-link size-3.5" />
      </a>
      <span
        class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform flex-shrink-0"
        :class="{ '-rotate-90 rtl:rotate-90': collapsed }"
      />
    </header>
    <div
      v-show="!collapsed"
      class="flex flex-col gap-3 px-3 pt-1 pb-3 overflow-y-auto max-h-[42vh]"
    >
      <component :is="view" v-bind="viewProps" />
      <PublishedReply
        v-if="supportsReplyEditing && publishedReply"
        :conversation-id="conversation.id"
        :message="publishedReply"
        :reply-meta="source.review?.reply || {}"
      />
      <div
        v-if="isReview || (supportsReplyEditing && !publishedReply)"
        class="flex flex-wrap items-center gap-2"
      >
        <span
          v-if="supportsReplyEditing && !publishedReply"
          class="inline-flex items-center gap-1.5 text-xs text-n-slate-11"
        >
          <span class="i-lucide-info size-3.5" />
          {{ t('MULTICHAT.SOURCE.REPLY.NOT_PUBLISHED') }}
        </span>
        <span class="flex-1" />
        <Button
          v-if="isReview"
          :label="t('MULTICHAT.SOURCE.COMPLAINT.BUTTON')"
          icon="i-lucide-flag"
          size="xs"
          variant="faded"
          color="ruby"
          :disabled="!canComplain"
          @click="openComplaint"
        />
      </div>
    </div>
    <ComplaintDialog
      v-if="isReview"
      ref="complaintDialogRef"
      :conversation-id="conversation.id"
    />
  </section>
</template>
